import React, { FunctionComponent, useContext, useState } from 'react'
import { css } from '@emotion/react'
import {
  AlertOutlined,
  AppstoreOutlined,
  BookOutlined,
  CloseOutlined,
  LoadingOutlined,
  LogoutOutlined,
  MergeOutlined,
  TagOutlined,
  UnorderedListOutlined
} from '@ant-design/icons'
import { Badge, Menu } from 'antd'
import { useNavigate } from '@tanstack/react-router'
import { MapSettingsContext } from '../../../contexts/MapSettingsContext'
import MapSettingsContextInterface from '../../../contexts/MapSettingsContext/MapSettingsContextInterface'
import { AuthContext } from '@strac/react-client-auth'
import { backgroundColor } from '../../../theme'
import ChangeLog from '../../../components/ChangeLog/ChangeLog'
import { orange, red } from '@ant-design/colors'
import { useFeathersClient } from '@strac/react-client-feathers'
import { useQuery } from '@tanstack/react-query'
import useQueryListener from '../../../hooks/data/useQueryListener'

interface IProps {
  selectedKey: string
  setSelectedKey: (key: string) => void
  collapsed: boolean
}

const MainMenu: FunctionComponent<any> = (props: IProps) => {
  const navigate = useNavigate()

  const client = useFeathersClient()

  const { setSelectedKey, selectedKey, collapsed } = props

  const { authUser } = useContext(AuthContext) as any

  const { selectedCollections, selectedVehicleHistory, setSelectedVehicleHistory } = useContext(
    MapSettingsContext
  ) as MapSettingsContextInterface

  const [changeLog, setChangeLog] = useState<boolean>(false)

  const [loading, setLoading] = useState<boolean>(false)

  const geofenceQuery = {
    user_id: authUser.id,
    active: true,
    $limit: 0
  }
  const { data: geofenceCount, refetch: refetchGeofenceCount } = useQuery({
    queryKey: ['geofence', 'find', geofenceQuery],
    queryFn: () =>
      client
        .service('geofence')
        .find({
          query: geofenceQuery
        })
        .then((response: any) => response.total),
    placeholderData: 0
  })

  useQueryListener('geofence', refetchGeofenceCount)

  const items: any = [
    {
      key: 'devices',
      icon: selectedKey === 'devices' ? <CloseOutlined /> : <UnorderedListOutlined />,
      label: 'Devices',
      style: {
        backgroundColor: backgroundColor,
        color: selectedKey === 'devices' ? red[5] : ''
      }
    },
    {
      key: 'collections',
      icon:
        selectedKey === 'collections' ? (
          <CloseOutlined />
        ) : (
          <Badge offset={[5, 6]} count={selectedCollections.length} dot={!collapsed} size={'small'}>
            <TagOutlined />
          </Badge>
        ),
      label: 'Collections',
      style: {
        backgroundColor: backgroundColor,
        color: selectedKey === 'collections' ? red[5] : ''
      }
    },
    {
      type: 'divider'
    },
    {
      key: 'geofences',
      icon:
        selectedKey === 'geofences' ? (
          <CloseOutlined />
        ) : (
          <Badge offset={[5, 6]} count={geofenceCount} dot={!collapsed} size={'small'}>
            <AlertOutlined />
          </Badge>
        ),
      label: 'Geofences',
      style: {
        backgroundColor: backgroundColor,
        color: selectedKey === 'geofences' ? red[5] : ''
      }
    } /*,
    {
      key: 'directions',
      icon: <CompassOutlined />,
      label: 'Directions',
      style: {
        backgroundColor: backgroundColor,
        color: selectedKey === 'directions' ? orange[5] : ''
      }
    }*/,
    {
      key: 'changelog',
      icon: <MergeOutlined />,
      label: 'Change Log',
      style: {
        border: '1px solid #1890ff',
        backgroundColor: backgroundColor,
        color: '#1890ff',
        position: 'absolute',
        bottom: '45px'
      },
      onClick: () => setChangeLog(true)
    },
    {
      key: '/logout',
      icon: loading && selectedKey === '/logout' ? <LoadingOutlined /> : <LogoutOutlined />,
      label: 'Logout',
      danger: true,
      style: {
        border: '1px solid red',
        position: 'absolute',
        bottom: 0
      },
      onClick: () => {
        setLoading(true)
        client.logout().then(() => {
          localStorage.removeItem('auth')
          setSelectedKey('/logout')
          navigate({ to: '/login' }).then(() => setLoading(false))
        })
      }
    }
  ]

  if (authUser.system_admin) {
    items.unshift({
      key: '/admin',
      icon: loading && selectedKey === '/admin' ? <LoadingOutlined /> : <AppstoreOutlined />,
      label: 'Admin',
      style: {
        backgroundColor: backgroundColor,
        color: selectedKey === '/admin' ? orange[5] : ''
      }
    })
  }

  if (selectedVehicleHistory) {
    items.unshift({
      type: 'divider'
    })
    items.unshift({
      key: 'vehicle-history',
      icon:
        selectedKey === 'vehicle-history' ? (
          <CloseOutlined />
        ) : loading ? (
          <LoadingOutlined />
        ) : (
          <BookOutlined />
        ),
      label: 'Vehicle History',
      style: {
        backgroundColor: backgroundColor,
        color: selectedKey === 'vehicle-history' ? red[5] : ''
      }
    })
  }

  return (
    <>
      <Menu
        onClick={(e: any) => {
          const { key } = e
          if (key === '/admin') {
            setSelectedKey(key)
            setLoading(true)
            navigate({ to: key }).then(() => setLoading(false))
          } else if (key === 'vehicle-history') {
            if (key === selectedKey) {
              setSelectedVehicleHistory(undefined)
            }
          }
          setSelectedKey(selectedKey === key ? '' : key)
        }}
        theme={'dark'}
        css={css`
          background-color: ${backgroundColor};
        `}
        mode={'inline'}
        items={items}
      />
      <ChangeLog open={changeLog} setOpen={setChangeLog} />
    </>
  )
}

export default MainMenu
